import {
    useReactFlow,
    getOutgoers,
} from '@xyflow/react';
import { useCallback, useMemo } from 'react';

export const ContextMenu = ({
    t,
    id,
    type,
    top,
    left,
    right,
    bottom,
    ...props
}) => {
    const { getNode, setNodes, addNodes, setEdges, getNodes, addEdges, getEdges } = useReactFlow();

    const canAddNode = useMemo(() => {
        if (type === 'endNode') return false;

        const nodes = getNodes();
        const edges = getEdges();
        const outgoers = getOutgoers({ id }, nodes, edges)

        // startNode and userNode can only connect once
        if (type === 'startNode' || type === 'userNode') return outgoers.length === 0

        // BotNode can connect to many userNodes, but only once to endNode
        else if (type === 'botNode') {
            const endNode = outgoers.find((node) => node.type === 'endNode');
            return !endNode
        }
    }, [id, getNodes, getEdges])

    const canEnd = useMemo(() => {
        if (type !== 'botNode') return false

        const nodes = getNodes()
        const edges = getEdges()
        const outgoers = getOutgoers({ id }, nodes, edges)
        return outgoers.length === 0
    }, [id, getNodes, getEdges])

    const canDelete = type !== 'startNode'

    const addNode = useCallback(() => {
        if (!canAddNode) return;

        const currentNode = getNode(id);
        const nodes = getNodes();
        const position = {
            x: currentNode.position.x + currentNode.measured.width / 3,
            y: currentNode.position.y + currentNode.measured.height + 40,
        };

        let newNodeType = currentNode.type === 'botNode' ? 'userNode' : 'botNode'
        let nextId = nodes.reduce((max, node) => Math.max(max, +node.id || 0), 0) + 1

        addNodes({
            id: `${nextId}`,
            position,
            type: newNodeType,
            data: {}
        });
        const edgeId = currentNode.type === 'startNode' ? 'startEdge' : `e${currentNode.id}-${nextId}`
        addEdges({
            id: edgeId,
            source: `${currentNode.id}`,
            target: `${nextId}`
        })
    }, [id, canAddNode, getNodes, getNode, addNodes, addEdges]);

    const addFinalNode = useCallback(() => {
        if (!canEnd) return;

        const currentNode = getNode(id);
        const nodes = getNodes();
        const position = {
            x: currentNode.position.x + currentNode.measured.width / 2 + 40,
            y: currentNode.position.y + currentNode.measured.height + 40,
        };

        addNodes({
            id: `end${nodes.length}`,
            type: 'endNode',
            position,
        });
        addEdges({
            id: `e${currentNode.id}-end${nodes.length}`,
            source: `${currentNode.id}`,
            target: `end${nodes.length}`
        })
    }, [id, canEnd, getNodes, getNode, addNodes, addEdges]);

    const deleteNode = useCallback(() => {
        setNodes((nodes) => nodes.filter((node) => node.id !== id));
        setEdges((edges) => edges.filter((edge) => edge.source !== id && edge.target !== id));
    }, [id, getNodes, setNodes, setEdges]);

    if (!canAddNode) bottom += 32 // Remove space of add button
    if (!canEnd) bottom += 32 // Remove space of finalize button
    if (!canDelete) bottom += 32 // Remove space of delete button

    if (!canAddNode && !canEnd && !canDelete) return <></>

    return (
        <div
            style={{
                top,
                right,
                bottom,
                left
            }}
            className="context-menu"
            {...props}
        >
            {
                canAddNode &&
                <button onClick={addNode}><i className="k-icon k-i-plus"></i> <span>{t("contextMenu.node")}</span></button>
            }
            {
                canEnd &&
                <button onClick={addFinalNode}><i className="k-icon k-i-stop-sm"></i> <span>{t("contextMenu.end")}</span></button>
            }
            {
                canDelete &&
                <button onClick={deleteNode}><i className="k-icon k-i-trash"></i> <span>{t("contextMenu.delete")}</span></button>
            }
        </div>
    );
}

export const EdgeContextMenu = ({
    t,
    id,
    top,
    left,
    right,
    bottom,
    ...props
}) => {
    const { setEdges } = useReactFlow();

    const deleteEdge = useCallback(() => {
        setEdges((edges) => edges.filter((edge) => edge.id !== id));
    }, [id, setEdges]);

    return (
        <div
            style={{
                top,
                right,
                bottom,
                left
            }}
            className="context-menu"
            {...props}
        >
            <button onClick={deleteEdge}><i className="k-icon k-i-trash"></i> <span>{t("contextMenu.delete")}</span></button>
        </div>
    );
}